import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import SimilarPropertiesModal from 'src/components/similar-properties-modal/SimilarPropertiesModal';
import {
    findSimilarProperties,
    getDefaultFilters,
    getLeadSignals,
    isPetExcluded,
    isVoucherExcluded,
    type ILeadSignals,
    type ISimilarProperty,
} from 'src/components/similar-properties-modal/find-similar-properties';
/*
 * Leaf imports rather than the `src/services/api` barrel: the barrel re-exports
 * every API module, including ones that construct request URLs at module-load time,
 * so pulling it into a component as narrowly-scoped as this one risks eagerly
 * evaluating unrelated modules a caller never touches.
 */
import { useProperties } from 'src/services/api/properties/use-properties';
import type { ILead } from 'src/services/api/leads/types';
import type { IProperty } from 'src/services/api/properties/types';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';

/** A property this far over budget no longer reads as "comparable", it reads as "too expensive". */
const PRICED_OUT_TOLERANCE = 1.15;

/*
 * Module scope, not a ref: the provider sits under the messages page's loading
 * boundary, which unmounts it while the next lead's data loads. A provider-local Set
 * would be discarded there, so A -> B -> A would force A's tab a second time. Keyed by
 * lead uuid and only ever added to, so it stays bounded by the leads visited in one
 * session and resets with the page.
 */
const switchedForLeads = new Set<string>();

type ISimilarPropertiesContextValue = {
    isEnabled: boolean;
    anchor: IProperty | null;
    signals: ILeadSignals;
    matches: ISimilarProperty[];
    /** True when a real conflict exists between the lead's signals and its assigned property — the nudge's trigger. */
    hasMismatch: boolean;
    openSimilarProperties: (activeId?: number | null) => void;
};

const SimilarPropertiesContext = createContext<ISimilarPropertiesContextValue | null>(null);

type Props = {
    lead: ILead;
    property?: IProperty | null;
    /** Appends text to the composer draft; wired to the modal's "Add to message" action. */
    onInsertMessage: (text: string) => void;
    children: React.ReactNode;
};

/**
 * Owns the single similar-properties modal instance for the conversation page, so the
 * sidebar chip, the composer nudge, and the composer toolbar button all open the same
 * map rather than each keeping (and paying for) its own copy.
 */
export function SimilarPropertiesProvider({ lead, property, onInsertMessage, children }: Props) {
    const isEnabled = useIsFeatureEnabled(EFeatureFlag.X_SELL);
    const anchor = property ?? null;
    const { data: properties = [] } = useProperties({ disabled: !isEnabled });

    const signals = useMemo(() => getLeadSignals(lead, anchor), [lead, anchor]);
    const matches = useMemo(
        () => (isEnabled ? findSimilarProperties(anchor, properties, getDefaultFilters(anchor), signals).matches : []),
        [isEnabled, anchor, properties, signals]
    );

    /*
     * A hard filter (pet ban, voucher ban) or a hard fact (not vacant) is a real
     * conflict; a rent merely above budget is not — only a rent far enough over to no
     * longer read as "comparable" counts, which is what the 1.15x tolerance draws the
     * line at.
     */
    const hasMismatch = useMemo(() => {
        if (anchor == null) {
            return false;
        }
        const petPolicy = anchor.facts?.pet_policy?.value;
        const petConflict = signals.petType != null &&
            typeof petPolicy === 'string' &&
            isPetExcluded(signals.petType, petPolicy);
        const voucherConflict = signals.voucherType != null &&
            isVoucherExcluded(signals.voucherType, anchor.accepted_vouchers);
        const pricedOut = signals.monthlyBudget != null &&
            anchor.rent != null &&
            anchor.rent > signals.monthlyBudget * PRICED_OUT_TOLERANCE;
        const notVacant = anchor.is_vacant === false;
        return petConflict || voucherConflict || pricedOut || notVacant;
    }, [anchor, signals]);

    const [activeId, setActiveId] = useState<number | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const openSimilarProperties = useCallback((id: number | null = null) => {
        setActiveId(id);
        setIsOpen(true);
    }, []);

    /*
     * Mirrors the tours auto-switch in Nudges.tsx: fires once per key (there the
     * suggestion id, here `lead.uuid`, since a mismatch has no id of its own),
     * uses replace: true so it doesn't grow the history stack, and hardcodes the
     * tab string because SidebarTab is module-private to LeadSidebar.tsx and stays
     * that way — this isn't reason enough to export it. Same precedent, no
     * manual-choice guard: the tours version has none either, and the visited-leads
     * Set means this never re-fights a manual choice for the same lead anyway.
     */
    const [, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (isEnabled && hasMismatch && matches.length > 0 && !switchedForLeads.has(lead.uuid)) {
            switchedForLeads.add(lead.uuid);
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('sidebarTab', 'xsell');
                return next;
            }, { replace: true });
        }
    }, [isEnabled, hasMismatch, matches.length, lead.uuid, setSearchParams]);

    const value = useMemo<ISimilarPropertiesContextValue>(() => ({
        isEnabled,
        anchor,
        signals,
        matches,
        hasMismatch,
        openSimilarProperties,
    }), [
        isEnabled,
        anchor,
        signals,
        matches,
        hasMismatch,
        openSimilarProperties,
    ]);

    return (
        <SimilarPropertiesContext.Provider value={value}>
            {children}
            {isOpen && (
                <SimilarPropertiesModal
                    anchor={anchor}
                    signals={signals}
                    initialActiveId={activeId}
                    onInsertMessage={onInsertMessage}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </SimilarPropertiesContext.Provider>
    );
}

/**
 * Nullable on purpose: the lead sidebar (and the property tab it wraps) also renders
 * from the leads list page, which never mounts this provider. Callers outside the
 * conversation page treat a null context as "the feature isn't wired up here".
 */
export function useSimilarPropertiesContext(): ISimilarPropertiesContextValue | null {
    return useContext(SimilarPropertiesContext);
}
